(() => {
  if (window.createBacksheetController) return;

  const getScrollLockState = () => {
    if (!window.__backsheetScrollLockState) {
      window.__backsheetScrollLockState = {
        count: 0,
        scrollY: 0,
        styles: null,
      };
    }

    return window.__backsheetScrollLockState;
  };

  const applyDefaultScrollLock = () => {
    const lockState = getScrollLockState();
    lockState.count += 1;
    if (lockState.count > 1) return;

    const { documentElement, body } = document;
    lockState.scrollY = window.scrollY || window.pageYOffset || 0;
    lockState.styles = {
      htmlOverflow: documentElement.style.overflow,
      htmlOverscrollBehavior: documentElement.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyTouchAction: body.style.touchAction,
      bodyOverscrollBehavior: body.style.overscrollBehavior,
    };

    documentElement.style.overflow = 'hidden';
    documentElement.style.overscrollBehavior = 'none';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${lockState.scrollY}px`;
    body.style.width = '100%';
    body.style.left = '0';
    body.style.right = '0';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'none';
  };

  const releaseDefaultScrollLock = () => {
    const lockState = getScrollLockState();
    if (lockState.count === 0) return;

    lockState.count -= 1;
    if (lockState.count > 0) return;

    const { documentElement, body } = document;
    const savedStyles = lockState.styles || {};

    documentElement.style.overflow = savedStyles.htmlOverflow || '';
    documentElement.style.overscrollBehavior = savedStyles.htmlOverscrollBehavior || '';
    body.style.overflow = savedStyles.bodyOverflow || '';
    body.style.position = savedStyles.bodyPosition || '';
    body.style.top = savedStyles.bodyTop || '';
    body.style.width = savedStyles.bodyWidth || '';
    body.style.left = savedStyles.bodyLeft || '';
    body.style.right = savedStyles.bodyRight || '';
    body.style.touchAction = savedStyles.bodyTouchAction || '';
    body.style.overscrollBehavior = savedStyles.bodyOverscrollBehavior || '';

    window.scrollTo(0, lockState.scrollY || 0);
    lockState.styles = null;
    lockState.scrollY = 0;
  };

  window.createBacksheetController = function createBacksheetController(config) {
    const {
      host,
      overlay,
      panel,
      closeBtn,
      swipeHandle,
      openTriggers = [],
      visibleClass = 'is-visible',
      moveBackDelay = 300,
      swipeThreshold = 24,
      swipeUpLimit = -36,
      lock,
      unlock,
      shouldEnableSwipe = () => true,
      onOpen,
      onClose,
    } = config;

    const state = {
      isClosing: false,
      closeTimer: null,
      swipeStartY: 0,
      swipeCurrentY: 0,
      isSwiping: false,
    };

    const triggers = Array.from(openTriggers || []);
    const lockFn = typeof lock === 'function' ? lock : null;
    const unlockFn = typeof unlock === 'function' ? unlock : null;

    const lockScreen = () => {
      applyDefaultScrollLock();
      lockFn?.();
    };

    const unlockScreen = () => {
      unlockFn?.();
      releaseDefaultScrollLock();
    };

    const resetPanelDrag = () => {
      if (!panel) return;
      panel.style.transition = '';
      panel.style.transform = '';
      state.isSwiping = false;
      state.swipeStartY = 0;
      state.swipeCurrentY = 0;
    };

    const moveOverlayBack = () => {
      if (!overlay?.isConnected || !host?.isConnected) return;
      if (overlay.parentNode !== host) {
        host.appendChild(overlay);
      }
      state.isClosing = false;
      onClose?.();
    };

    const open = () => {
      if (!overlay) return;
      state.isClosing = false;
      window.__syncViewportHeight?.();
      if (overlay.parentNode !== document.body) {
        document.body.appendChild(overlay);
      }
      resetPanelDrag();
      onOpen?.();
      requestAnimationFrame(() => {
        overlay.classList.add(visibleClass);
      });
      overlay.setAttribute('aria-hidden', 'false');
      lockScreen();
    };

    const close = () => {
      if (!overlay || state.isClosing) return;
      state.isClosing = true;

      resetPanelDrag();

      // Move focus somewhere safe (button that opened the backsheet overlay)
      overlay.parentNode?.focus();

      overlay.classList.remove(visibleClass);
      unlockScreen();

      clearTimeout(state.closeTimer);
      state.closeTimer = setTimeout(() => {
        overlay.setAttribute('aria-hidden', 'true');
        moveOverlayBack();
      }, moveBackDelay);
    };

    const onKeydown = (event) => {
      if (event.key === 'Escape') {
        close();
      }
    };

    const onOverlayClick = (event) => {
      if (event.target === overlay) {
        close();
      }
    };

    const onSwipeTouchStart = (event) => {
      if (!shouldEnableSwipe() || !overlay?.classList.contains(visibleClass) || !panel) return;
      if (!event.touches?.length) return;
      state.isSwiping = true;
      state.swipeStartY = event.touches[0].clientY;
      state.swipeCurrentY = state.swipeStartY;
      panel.style.transition = 'none';
    };

    const onSwipeTouchMove = (event) => {
      if (!state.isSwiping || !panel) return;
      if (!event.touches?.length || !overlay?.classList.contains(visibleClass)) return;
      state.swipeCurrentY = event.touches[0].clientY;
      const deltaY = state.swipeCurrentY - state.swipeStartY;
      const clampedY = Math.max(swipeUpLimit, deltaY);
      panel.style.transform = `translateY(${clampedY}px)`;
      event.preventDefault();
    };

    const onSwipeTouchEnd = () => {
      if (!state.isSwiping || !panel) return;
      const deltaY = state.swipeCurrentY - state.swipeStartY;
      state.isSwiping = false;
      state.swipeStartY = 0;
      state.swipeCurrentY = 0;
      panel.style.transition = '';

      if (deltaY >= swipeThreshold) {
        close();
        return;
      }

      panel.style.transform = '';
    };

    const bind = () => {
      triggers.forEach((trigger) => trigger?.addEventListener('click', open));
      overlay?.addEventListener('click', onOverlayClick);
      closeBtn?.addEventListener('click', close);
      swipeHandle?.addEventListener('touchstart', onSwipeTouchStart, { passive: true });
      swipeHandle?.addEventListener('touchmove', onSwipeTouchMove, { passive: false });
      swipeHandle?.addEventListener('touchend', onSwipeTouchEnd, { passive: true });
      swipeHandle?.addEventListener('touchcancel', onSwipeTouchEnd, { passive: true });
      document.addEventListener('keydown', onKeydown);
    };

    const unbind = () => {
      triggers.forEach((trigger) => trigger?.removeEventListener('click', open));
      overlay?.removeEventListener('click', onOverlayClick);
      closeBtn?.removeEventListener('click', close);
      swipeHandle?.removeEventListener('touchstart', onSwipeTouchStart);
      swipeHandle?.removeEventListener('touchmove', onSwipeTouchMove);
      swipeHandle?.removeEventListener('touchend', onSwipeTouchEnd);
      swipeHandle?.removeEventListener('touchcancel', onSwipeTouchEnd);
      document.removeEventListener('keydown', onKeydown);
      clearTimeout(state.closeTimer);
      unlockScreen();
      resetPanelDrag();
      state.isClosing = false;
    };

    return { bind, unbind, open, close, resetPanelDrag };
  };
})();
